import * as Phaser from 'phaser';
import { gameConfig } from './config/game.config';
import { installTestSeams } from './debug/testSeams';
import { mountDevMusicPanel } from './debug/DevMusicPanel';
import { isC5UiEnabled } from './ui/c5/c5Flag';
import { mountC5Frame } from './ui/c5/C5Frame';

// Phaser 4.0.0 ESM bug: a few internal classes (notably SpriteGPULayer)
// reference the `Phaser` namespace as a UMD-style global rather than via
// the ESM import. Re-exposing the namespace on `window` keeps that legacy
// access path working until upstream is patched. Tracked: phaser issue
// queue — remove this shim when upgrading past the fix.
(window as unknown as Record<string, unknown>).Phaser = Phaser;

// Wait for web fonts (Almendra, OpenDyslexic) to finish loading before
// creating the Phaser canvas.  This prevents a one-frame flash where text
// renders in the fallback system font before the typeface is ready.
// document.fonts.ready resolves immediately if fonts were already loaded
// from cache, so there is no perceptible delay on repeat visits.
interface ImportMetaEnvLike { env?: { DEV?: boolean } }
const isDev = ((import.meta as unknown as ImportMetaEnvLike).env?.DEV ?? false) === true;

// Race document.fonts.ready against a 3s timeout so the game boots on mobile
// even when Google Fonts CDN is slow or blocked (e.g. content blockers, poor
// connectivity). Fonts that arrive late simply pop in via font-display: swap.
const fontsOrTimeout = Promise.race([
  document.fonts.ready,
  new Promise<void>((resolve) => setTimeout(resolve, 3000)),
]);

fontsOrTimeout.then(() => {
  // C5 "Indigo & Ink" frame (docs/design/ui-c5-indigo-ink.md). Mounted BEFORE
  // the game so #game-root is already in its plate slot when Phaser measures
  // the parent. Flag off = the canvas fills the viewport exactly as before.
  const c5Frame = isC5UiEnabled() ? mountC5Frame() : null;

  const game = new Phaser.Game(gameConfig);
  (window as unknown as Record<string, unknown>).__PHASER__ = game;
  c5Frame?.attach(game);
  installTestSeams(game);
  if (isDev) mountDevMusicPanel();

  // Pause the game loop when the browser tab is hidden to eliminate
  // cross-tab CPU/GPU drain (AGE-1206).
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      game.loop.sleep();
    } else {
      game.loop.wake();
    }
  });
});
